// Geometry for WRF wind-direction arrows.
//
// `wind_direction.json` (one file per WRF_Wind forecast frame) is an array of
// segments [lng1, lat1, lng2, lat2] — the shaft from a grid point (start) to
// the wind-direction tip. We render the shaft plus an open "V" arrowhead at the
// tip — an open arrowhead like Gmap's FORWARD_OPEN_ARROW (proportions below are
// hand-tuned, not measured from Gmap).

export type WindSegment = [number, number, number, number];

const HEAD_FRACTION = 0.4; // arrowhead length as a fraction of the shaft
const HEAD_ANGLE_DEG = 25; // half-angle of the open arrowhead

// Returns Leaflet [lat, lng] points for one arrow: [start, tip, barbLeft, tip,
// barbRight] — the shaft followed by an open arrowhead retraced through the tip.
// The head is computed in a local planar approximation (longitude scaled by
// cos(latitude)) so its on-screen angle stays consistent across the map's
// latitude range, then converted back to lat/lng.
export const windArrowPositions = (seg: WindSegment): [number, number][] => {
  const [lng1, lat1, lng2, lat2] = seg;
  const start: [number, number] = [lat1, lng1];
  const tip: [number, number] = [lat2, lng2];

  const latMid = (lat1 + lat2) / 2;
  const k = Math.cos((latMid * Math.PI) / 180) || 1;
  const dx = (lng2 - lng1) * k;
  const dy = lat2 - lat1;
  const len = Math.hypot(dx, dy);
  if (len === 0) return [start, tip];

  const ux = dx / len;
  const uy = dy / len;
  const b = len * HEAD_FRACTION;
  const a = (HEAD_ANGLE_DEG * Math.PI) / 180;

  // Reverse direction (tip → back along shaft), rotated ±a.
  const rot = (cx: number, cy: number, ang: number): [number, number] => [
    cx * Math.cos(ang) - cy * Math.sin(ang),
    cx * Math.sin(ang) + cy * Math.cos(ang),
  ];
  const [lxs, lys] = rot(-ux, -uy, a);
  const [rxs, rys] = rot(-ux, -uy, -a);

  // Convert back to lat/lng (undo the cos(lat) scaling on the lng axis).
  const barbLeft: [number, number] = [lat2 + lys * b, lng2 + (lxs * b) / k];
  const barbRight: [number, number] = [lat2 + rys * b, lng2 + (rxs * b) / k];

  return [start, tip, barbLeft, tip, barbRight];
};
